export const PROMISE_CENTER_STATUS_LABELS = {
  DRAFT: "Draft", ACTIVE: "Active", PENDING_VERIFICATION: "Pending Verification", KEPT: "Kept",
  PARTIALLY_KEPT: "Partially Kept", NOT_KEPT: "Not Kept", CANCELLED: "Cancelled", EXPIRED: "Expired",
  REVERSED: "Reversed", MANUAL_REVIEW_REQUIRED: "Manual Review",
} as const;

export type PromiseCenterStatus = keyof typeof PROMISE_CENTER_STATUS_LABELS;
export type PromiseDueState = "DUE_TODAY" | "OVERDUE" | "UPCOMING";

export interface PromiseCenterRecord {
  id: string; companyId: string; clientId: string; customerId: string; accountId: string; placementId: string | null;
  promiseReference: string; promisedAmountCents: number; currency: string; promiseDate: string; dueDate: string;
  status: PromiseCenterStatus; statusLabel: string; dueState: PromiseDueState | null; dueStateLabel: string | null; outcome: string | null;
  operationalTimezone: string; operationalDate: string;
  source: string; assignedOwnerId: string | null; ownerName: string; confirmedBy: string | null; confirmedAt: string | null;
  createdBy: string | null; createdAt: string; updatedAt: string; version: number;
  client: { number: string; name: string }; customer: { number: string; name: string };
  account: { number: string; status: string; balanceCents: number };
}

export interface PromiseCenterListResult {
  records: PromiseCenterRecord[]; page: number; pageSize: number; total: number;
  operationalTimezone: "PER_CLIENT"; operationalDate: null;
  summary: { total: number; active: number; dueToday: number; overdue: number; upcoming: number; pendingVerification: number; kept: number; notKept: number; activeAmountCents: number };
  owners: Array<{ id: string; name: string }>;
}

export interface PromiseCenterDetailResult {
  record: PromiseCenterRecord;
  invoiceAssociation: null | { invoiceId: string; invoiceNumber: string };
  history: Array<{ version: number; changedBy: string; event: string; correlationId: string; createdAt: string; snapshot: Record<string, unknown> }>;
}

export interface PromiseCenterListInput {
  page?: number; pageSize?: number; search?: string; status?: string; ownerId?: string; accountId?: string;
  customerId?: string; dueState?: string; dateFrom?: string; dateTo?: string; sort?: string;
}

export async function listPromiseCenter(input: PromiseCenterListInput = {}) {
  const params = new URLSearchParams({ page: String(input.page || 1), pageSize: String(input.pageSize || 25) });
  for (const key of ["search", "status", "ownerId", "accountId", "customerId", "dueState", "dateFrom", "dateTo", "sort"] as const) {
    if (input[key]) params.set(key, String(input[key]));
  }
  return request<PromiseCenterListResult>(`/api/ar-recovery/promises?${params}`);
}

export async function getPromiseCenter(promiseId: string) {
  return request<PromiseCenterDetailResult>(`/api/ar-recovery/promises/${encodeURIComponent(promiseId)}`);
}

export async function updatePromiseCenter(
  promiseId: string,
  input: { expectedVersion: number; action: string; changes?: Record<string, unknown>; idempotencyKey: string },
) {
  return request<{ record: PromiseCenterRecord; replay: boolean }>(`/api/ar-recovery/promises/${encodeURIComponent(promiseId)}`, {
    method: "PATCH", body: JSON.stringify(input),
  });
}

async function request<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, { credentials: "same-origin", headers: init?.body ? { "content-type": "application/json" } : undefined, ...init });
  if (!response.ok) {
    const message = (await response.text()).trim();
    throw new Error(message || `Promise Center request failed (${response.status}).`);
  }
  return response.json() as Promise<T>;
}
